import { Injectable } from '@angular/core';

import { combineLatest, forkJoin, of } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { filter, map, mapTo, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';

import { isEmpty, isEqual } from 'lodash-es';
import { BaseNestedFieldDefinition, DottedKey } from '../../../core/field-definitions/entities/field-definition.entity';
import { FieldDefinitionHelper } from '../../../core/field-definitions/helpers/field-definition.helper';
import { ShFieldDefinitionService } from '../../../core/field-definitions/services/field-definition.service';
import { ApiFragment } from '../../../core/records/entities/api-fragment.class';
import { MergeStrategy, Metadata } from '../../../core/records/entities/metadata/metadata.entity';
import { PartialRecord } from '../../../core/records/entities/partial-record.class';
import { TypedValue } from '../../../form-controls/components/form-typed-value-list/typed-values.entity';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';
import { FieldValue } from '../../entities/field-values/field-type-values.entity';
import { RecordFormValue } from '../../entities/record-form-values/record-form-value.entity';
import { FieldReadHelper } from '../fields/field-read.helper';
import { FieldWriteHelper } from '../fields/field-write.helper';
import { HasMetadataChanged } from './metadata-change-detection.helper';

type RecordValues = Record<string, FieldValue | FieldValue[]>;

@Injectable({ providedIn: 'root' })
export class FormMetadataHelper {
    constructor(
        public fieldReadHelper: FieldReadHelper,
        public fieldWriteHelper: FieldWriteHelper,
        public fieldDefinitionService: ShFieldDefinitionService
    ) {}

    /**
     * Generates ApiFragment for FormValues applied to single Record.
     * This will do the same as generateBulkMetadataSidecars, but will serially apply all changes to the provided record
     *
     * NOTE: Generally it is not recommended to use this to update existing records anymore. For creating new records this is ok.
     * For updating it is better to only send sidecars with actual changes to API to reduce potential errors and overwriting unintentionally.
     */
    public generateModifiedRecord(data: RecordFormValue, record: PartialRecord): Observable<ApiFragment> {
        const separatedData$: Observable<RecordFormValue[]> = this.separateDataTypes(data, [record]).pipe(
            shareReplay(1)
        );

        const partialRecord: PartialRecord = new PartialRecord(record.metadata, false, true, true);

        // Serially applies separate sidecars generated in generateBulkMetadataSidecars

        return separatedData$.pipe(
            switchMap(separatedData =>
                this.generateMetadataSidecar(
                    MergeStrategy.OVERWRITE,
                    separatedData[0], // Singular values
                    partialRecord,
                    FormValueState.UNSET
                )
            ),
            map(modifiedRecord => modifiedRecord ?? partialRecord),
            withLatestFrom(separatedData$),
            switchMap(([modifiedRecord, separatedData]) =>
                this.generateMetadataSidecar(
                    MergeStrategy.OVERWRITE,
                    separatedData[0], // Singular values
                    modifiedRecord,
                    FormValueState.SET
                )
            ),
            map(modifiedRecord => modifiedRecord ?? partialRecord),
            withLatestFrom(separatedData$),
            switchMap(([modifiedRecord, separatedData]) =>
                this.generateMetadataSidecar(
                    MergeStrategy.OVERWRITE,
                    separatedData[1], // List values
                    modifiedRecord,
                    FormValueState.SET
                )
            ),
            map(modifiedRecord => modifiedRecord ?? partialRecord),
            withLatestFrom(separatedData$),
            switchMap(([modifiedRecord, separatedData]) =>
                this.generateMetadataSidecar(
                    MergeStrategy.OVERWRITE,
                    separatedData[2], // Complex values
                    modifiedRecord as PartialRecord,
                    FormValueState.UNSET
                )
            ),
            map(modifiedRecord => modifiedRecord ?? partialRecord),
            withLatestFrom(separatedData$),
            switchMap(([modifiedRecord, separatedData]) =>
                this.generateMetadataSidecar(
                    MergeStrategy.OVERWRITE,
                    separatedData[2], // Complex values
                    modifiedRecord as PartialRecord,
                    FormValueState.SET
                )
            ),
            map(modifiedRecord => modifiedRecord ?? partialRecord),
            filter((modifiedRecord): modifiedRecord is PartialRecord => !!modifiedRecord),
            map<PartialRecord, ApiFragment>(modifiedRecord => new ApiFragment(modifiedRecord.metadata as Metadata))
        ) as Observable<ApiFragment>;
    }

    /**
     * Generates separate sidecar per MergeStrategy.
     * Used for Bulk editing.
     * OVERWRITE: Overwrite existing value if SET value provided. This is the default for 'singular' fields.
     * SUBTRACT: Remove the UNSET value(s) from 'list' fields, preserving the rest.
     * MERGE: Add the SET value(s) to 'list' fields, preserving the rest.
     *
     * KEEP: Not used in this instance since overwrite will just write the same value if it is the same.
     */
    public generateBulkMetadataSidecars(
        data: RecordFormValue,
        originalRecords: PartialRecord[]
    ): Observable<PartialRecord[]> {
        return this.separateDataTypes(data, originalRecords).pipe(
            switchMap(([singularValues, listValues, complexValues]) => {
                // SET singular values overwrite existing with new value
                const overwriteSimpleSet$: Observable<PartialRecord | null> = this.generateMetadataSidecar(
                    MergeStrategy.OVERWRITE,
                    singularValues,
                    new PartialRecord({}, false),
                    FormValueState.SET,
                    [],
                    true
                );

                // UNSET singular values 'empty' the field
                const overwriteSimpleUnset$: Observable<PartialRecord | null> = this.generateMetadataSidecar(
                    MergeStrategy.OVERWRITE,
                    singularValues,
                    new PartialRecord({}, false),
                    FormValueState.UNSET,
                    [],
                    true
                );

                // UNSET list-values are subtracted
                const subtractLists$: Observable<PartialRecord | null> = this.generateMetadataSidecar(
                    MergeStrategy.SUBTRACT,
                    listValues,
                    new PartialRecord({}, false),
                    FormValueState.UNSET,
                    [],
                    true
                );

                // SET list-values are merged
                const mergeList$: Observable<PartialRecord | null> = this.generateMetadataSidecar(
                    MergeStrategy.MERGE,
                    listValues,
                    new PartialRecord({}, false),
                    FormValueState.SET,
                    [],
                    true
                );

                const overwriteComplexSet$: Observable<PartialRecord | null> = this.generateMetadataSidecar(
                    MergeStrategy.OVERWRITE,
                    complexValues,
                    new PartialRecord({}, false),
                    FormValueState.SET,
                    [],
                    true
                );

                const overwriteComplexUnset$: Observable<PartialRecord | null> = this.generateMetadataSidecar(
                    MergeStrategy.SUBTRACT,
                    complexValues,
                    new PartialRecord({}, false),
                    FormValueState.UNSET,
                    [],
                    true
                );

                return combineLatest([
                    overwriteSimpleSet$,
                    overwriteSimpleUnset$,
                    subtractLists$,
                    mergeList$,
                    overwriteComplexSet$,
                    overwriteComplexUnset$
                ]);
            }),
            map(sidecars => sidecars.filter((sidecar): sidecar is PartialRecord => sidecar !== null))
        );
    }

    /**
     * Generates separate sidecar per MergeStrategy by providing MergeStrategy per field through TypedValue array.
     * Used for Batch editing.
     * NOTE: For each field the SET FormValues will be used and the provided MergeStrategy applied.
     * UNSET or INDETERMINATE values are ignored.
     *
     * OVERWRITE: Overwrite existing value if SET value provided.
     * SUBTRACT: Remove the SET value(s), preserving the rest.
     * MERGE: Add the SET value(s), preserving the rest.
     * KEEP: Unused for the time.
     */
    public generateBatchMetadataSidecars(
        data: RecordFormValue,
        fieldMergeStrategies: TypedValue[]
    ): Observable<PartialRecord[]> {
        const mergeStrategySidecarStreams: Observable<PartialRecord | null>[] = Object.keys(MergeStrategy).map(
            strategy =>
                this.generateMetadataSidecar(
                    strategy as MergeStrategy,
                    data,
                    new PartialRecord({}, false),
                    FormValueState.SET,
                    fieldMergeStrategies,
                    true
                )
        );

        // Allow clearing of simpleFields
        mergeStrategySidecarStreams.push(
            this.generateMetadataSidecar(
                MergeStrategy.OVERWRITE,
                data,
                new PartialRecord({}, false),
                FormValueState.UNSET,
                fieldMergeStrategies,
                true
            )
        );

        // Filter out empty sidecars

        return combineLatest(mergeStrategySidecarStreams).pipe(
            map(sidecars => sidecars.filter((sidecar): sidecar is PartialRecord => sidecar !== null))
        );
    }

    /**
     * Generate PartialRecord object for provided MergeStrategy from FormValue data of provided state
     * @param mergeStrategy MergeStrategy to set
     * @param data FormValues of all fields
     * @param partialRecord Initial sidecar state
     * @param withState FormValueState to filter (Optional: Defaults to SET)
     * @param fieldMergeStrategies  TypedValue list of manual overrides of mergeStrategy per field (Optional: Defaults to empty array)
     * @param bulk If bulk editing, some checks need to pass before changes are written to the sidecar (Optional: Defaults to false)
     * @returns sidecar of type PartialRecord or null if no data
     */
    public generateMetadataSidecar(
        mergeStrategy: MergeStrategy,
        data: RecordFormValue,
        partialRecord: PartialRecord,
        withState: FormValueState = FormValueState.SET,
        fieldMergeStrategies: TypedValue[] = [],
        bulk: boolean = false
    ): Observable<PartialRecord | null> {
        let fieldDataOfStrategy: RecordFormValue = data;

        if (fieldMergeStrategies.length > 0) {
            const dottedKeysOfStrategy: Set<string> = new Set(
                fieldMergeStrategies
                    .filter(typedValue => typedValue.type === mergeStrategy)
                    .map(typedValue => typedValue.value)
            );

            // Filter out fieldData for fields of specified MergeStrategy
            fieldDataOfStrategy = Object.fromEntries(
                Object.entries(data).filter(([key]) => dottedKeysOfStrategy.has(key))
            );
        }

        if (!fieldDataOfStrategy) {
            return of(null);
        }

        // Filter out FormValues with specified state
        const valuesOfState: RecordValues = this.getRecordFormValueFromEntries(
            fieldDataOfStrategy,
            withState,
            mergeStrategy,
            bulk
        );

        if (isEqual(valuesOfState, {})) {
            return of(null);
        }

        return this.setToRecord(valuesOfState, partialRecord, mergeStrategy);
    }

    /**
     * Separates form data into 'singular' and 'list' data
     * Singular: Text, Boolean, Date, Complex,... => all fields where there can be only 1 value at a time
     * List: MultiItem, List, ComplexList,... => all fields where there can be multiple values at a time
     */
    private separateDataTypes(data: RecordFormValue, originalRecords: PartialRecord[]): Observable<RecordFormValue[]> {
        const entries: [string, FormValue<any> | FormValue<any>[]][] = Object.entries(data);

        if (entries.length === 0) {
            return of([{}, {}, {}]);
        }

        const fieldDefinitions$: Observable<(BaseNestedFieldDefinition | string)[]> = forkJoin(
            entries.map(([key, value]) =>
                this.fieldDefinitionService
                    .getByDottedKey(key as DottedKey)
                    .pipe(map(fieldDef => (fieldDef ? fieldDef : key)))
            )
        );

        return fieldDefinitions$.pipe(
            map(fieldDefinitions => {
                const singularValues: RecordFormValue = {};
                const listValues: RecordFormValue = {};
                const complexValues: RecordFormValue = {};

                for (const definition of fieldDefinitions) {
                    if (typeof definition === 'string') {
                        console.error(`FormMetadataHelper:: property ${definition} not available as FieldDefinition`);
                        continue;
                    }

                    const fieldDefinition = definition as BaseNestedFieldDefinition;

                    const key = fieldDefinition.DottedKey;
                    const value = data[key];

                    const originalValue: FormValue<any> | FormValue<any>[] = this.fieldReadHelper.getMergedFieldValue(
                        fieldDefinition,
                        originalRecords
                    );

                    const isChanged: boolean = HasMetadataChanged(originalValue, value);

                    if (!isChanged) {
                        // Don't add unchanged fields to sidecars
                        continue;
                    }

                    const isFieldDefList: boolean = FieldDefinitionHelper.IsList(fieldDefinition);
                    const isFieldDefComplex: boolean = FieldDefinitionHelper.IsComplex(fieldDefinition);

                    // Lists take precedence
                    if (isFieldDefList) {
                        listValues[key] = value;
                    } else if (isFieldDefComplex) {
                        complexValues[key] = value;
                    } else {
                        singularValues[key] = value;
                    }
                }

                return [singularValues, listValues, complexValues];
            })
        );
    }

    /**
     * Filter out FormValues with specified state
     */
    private getRecordFormValueFromEntries(
        recordToFilter: RecordFormValue,
        stateToFilter: FormValueState,
        mergeStrategy: MergeStrategy,
        bulk: boolean = false
    ): RecordValues {
        const stateRecordValues: RecordValues = {};

        for (const [key, value] of Object.entries(recordToFilter)) {
            if (isEmpty(value)) {
                continue;
            }

            if (Array.isArray(value)) {
                const filteredMatch: FormValue<any>[] = value.filter(m => m.state === stateToFilter);
                const indeterminate: FormValue<any>[] = value.filter(m => m.state === FormValueState.INDETERMINATE);

                // Only set if there is matching state
                // Or when MergeStrategy is OVERWRITE, but not if all values in array are indeterminate
                // This is to preserve INDETERMINATE complex objects
                // MED-2509 either filteredMatch or indeterminate should contain values before setting
                // MED-2712 can't remove list field items
                if (
                    !bulk ||
                    filteredMatch.length > 0 ||
                    (mergeStrategy === MergeStrategy.OVERWRITE &&
                        indeterminate.length > 0 &&
                        indeterminate.length !== value.length)
                ) {
                    stateRecordValues[key] = filteredMatch.map(m => m.value);
                }
                continue;
            }

            if (value.state === stateToFilter) {
                stateRecordValues[key] = value.value;
            }
        }

        return stateRecordValues;
    }

    /**
     * Write FieldValue data to an APIFragment or PartialRecord with specified MergeStrategy
     */
    public setToRecord<F extends PartialRecord>(
        data: Record<DottedKey, FieldValue | FieldValue[]>,
        fragment: F,
        mergeStrategy: MergeStrategy = MergeStrategy.OVERWRITE
    ): Observable<F> {
        const dottedKeys: DottedKey[] = Object.keys(data) as DottedKey[];
        const operations: Observable<F | undefined>[] = dottedKeys.map(dottedKey =>
            this.writeFieldWrapper(dottedKey, data[dottedKey], fragment, mergeStrategy)
        );

        return forkJoin(operations).pipe(mapTo(fragment));
    }

    /**
     * Write FieldValue data to field of APIFragment or PartialRecord with specified MergeStrategy
     * The FieldWriteHelper filters internally on the FormValueState depending on the specified MergeStrategy
     */
    private writeFieldWrapper<F extends PartialRecord>(
        dottedKey: DottedKey,
        value: FieldValue | FieldValue[],
        fragment: F,
        mergeStrategy: MergeStrategy
    ): Observable<F | undefined> {
        const fieldDefinition$: Observable<BaseNestedFieldDefinition | undefined> =
            this.fieldDefinitionService.getByDottedKey(dottedKey);

        return fieldDefinition$.pipe(
            filter((fieldDefinition): fieldDefinition is BaseNestedFieldDefinition => {
                if (fieldDefinition === undefined) {
                    console.error(`FormMetadataHelper:: property ${dottedKey} not available as FieldDefinition`);
                    return false;
                }
                return true;
            }),
            map(fieldDefinition => this.fieldWriteHelper.writeField(fieldDefinition, fragment, value, mergeStrategy))
        );
    }
}
